import { reactive, shallowRef } from 'vue'
import type { ParsedWaylinePreview } from '../utils/wayline-kmz'
import type { CesiumWaylinePlan } from '../utils/cesium-wayline'
import { prepareCesiumWaylinePreview } from '../utils/cesium-wayline'
import type { EditorHandler, EditorPosition, EditorShapes } from '../utils/cesium-scene'

// No Viewer/GL objects in Vue's deep reactive graph, and no Cesium object in
// root.$map. This is the hand-off between the route panel and its shared host.
export const cesiumWayline = shallowRef<CesiumWaylinePlan | null>(null)
export const cesiumMapState = reactive({
  ready: false,
  error: '',
  waylineName: '',
  waylineOwner: '',
  waylineRevision: 0,
  waylineFocusRevision: 0,
  editorRevision: 0,
  editorFocusRevision: 0,
  editorInteractionRevision: 0,
  /** Non-empty while the route editor owns the map; the host shows editing hints. */
  editorHint: '',
  flyToRevision: 0,
  followRevision: 0,
  trailRevision: 0,
  /** False while a route page opts out of 3D mapping models (the /wayline page). */
  modelsEnabled: true,
  /** True while the route editor owns the whole viewport; the workspace hides its
   * side column and the host hides its own toolbars. */
  editorFullscreen: false,
})

/** Camera and terrain commands the host exposes to the route editor. */
export interface CesiumSceneApi {
  zoom (direction: number): void
  /** Tilt the camera around the point it looks at: straight down or an oblique view. */
  tilt (mode: 'top' | 'oblique'): void
  view (mode: 'top' | 'oblique' | 'north' | 'rotate'): void
  fitEditor (): void
  flyToPosition (longitude: number, latitude: number, radius?: number): void
  /** False while the globe is the bare reference ellipsoid (no terrain configured or it failed to load). */
  hasTerrain (): boolean
  /** Terrain ellipsoid height under a WGS84 position from the rendered mesh, null while unknown. */
  groundHeight (longitude: number, latitude: number): number | null
  /** Terrain ellipsoid height from the most detailed tile, independent of the camera. */
  sampleTerrain (longitude: number, latitude: number): Promise<number | null>
  /** First-person camera: look out from a position along heading and pitch (degrees). */
  lookFrom (longitude: number, latitude: number, height: number, heading: number, pitch: number): void
  /** Current camera heading in degrees, 0 = north, clockwise. */
  cameraHeading (): number
}

export const cesiumSceneApi = shallowRef<CesiumSceneApi | null>(null)

export const cesiumTrail = shallowRef<EditorPosition[] | null>(null)

/** Replace the virtual aircraft's flown path; the host updates its line in place. */
export function setCesiumTrail (positions: EditorPosition[] | null): void {
  cesiumTrail.value = positions
  cesiumMapState.trailRevision++
}

export const cesiumFollowTarget = shallowRef<{ longitude: number; latitude: number; height: number } | null>(null)

/** Keep the host camera trailing a moving WGS84 position (virtual aircraft); null stops following. */
export function followCesiumPosition (target: { longitude: number; latitude: number; height: number } | null): void {
  cesiumFollowTarget.value = target
  cesiumMapState.followRevision++
}

export const cesiumFlyTarget = shallowRef<{ longitude: number; latitude: number; radius: number } | null>(null)

/** Ask the shared host to move its camera over a WGS84 position. */
export function flyCesiumTo (longitude: number, latitude: number, radius = 300): void {
  cesiumFlyTarget.value = { longitude, latitude, radius }
  cesiumMapState.flyToRevision++
}

// Editor overlay handed from the route editor panel to the shared host. The
// handler is a plain closure (not reactive) so pointer events stay cheap.
export const cesiumEditorShapes = shallowRef<EditorShapes | null>(null)
export const cesiumEditorInteraction: { handler: EditorHandler | null; draggableIds: Set<string> } = { handler: null, draggableIds: new Set() }

export function setCesiumEditorShapes (shapes: EditorShapes | null, fit = false): void {
  cesiumEditorShapes.value = shapes
  cesiumMapState.editorRevision++
  if (fit) cesiumMapState.editorFocusRevision++
}

export function setCesiumEditorInteraction (handler: EditorHandler | null, draggableIds: Iterable<string> = [], hint = ''): void {
  cesiumEditorInteraction.handler = handler
  cesiumEditorInteraction.draggableIds = new Set(draggableIds)
  cesiumMapState.editorHint = handler ? hint : ''
  cesiumMapState.editorInteractionRevision++
}

export function focusCesiumEditor (): void {
  cesiumMapState.editorFocusRevision++
}

export function clearCesiumEditor (): void {
  setCesiumEditorInteraction(null)
  setCesiumEditorShapes(null)
  setCesiumTrail(null)
}

export function setCesiumWayline (preview: ParsedWaylinePreview, name: string, owner: string, fit: boolean): string {
  const plan = prepareCesiumWaylinePreview(preview)
  cesiumMapState.waylineName = name
  cesiumMapState.waylineOwner = owner
  cesiumWayline.value = plan
  cesiumMapState.waylineRevision++
  if (fit) cesiumMapState.waylineFocusRevision++
  return plan.warning || ''
}

export function clearCesiumWayline (owner?: string): void {
  if (owner && owner !== cesiumMapState.waylineOwner) return
  cesiumWayline.value = null
  cesiumMapState.waylineName = ''
  cesiumMapState.waylineOwner = ''
  cesiumMapState.waylineRevision++
}
